// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Modal, Pressable, ScrollView, Image, ImageBackground, Platform } from 'react-native';
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router';

import { Text, View } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { useUser } from '@/app/user-context';
import { createAppLogger } from '@/services/appLogger';
import { getCurrentTourId, setCurrentTourId } from '@/services/currentTour';
import TourLeaderboardBg from '@/assets/images/tour-leader-board.png';
import TrophyImage from '@/assets/images/trophy.png';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import {
  getSharedBffWebSocket,
  type TourLeaderboardErrorEvent,
  type TourLeaderboardStateEvent,
  type TourLeaderboardUpdateEvent,
} from '@/services/bffWebSocket';
import { ResultsGraph } from '@/features/tour-leaderboard/components/ResultsGraph';
import { ResultsTable } from '@/features/tour-leaderboard/components/ResultsTable';
import { styles } from '@/features/tour-leaderboard/styles';
import type {
  GameDefinition,
  Player,
  RoundSummary,
  TourGameScore,
  TourRoundGameScore,
  TourRoundScoreboardResponse,
  TourScoreboardResponse,
  TourSummary,
  TourTotalsByHoleResponse,
} from '@/features/tour-leaderboard/types';
import {
  buildOrderedRounds,
  buildPlayerRows,
} from '@/features/tour-leaderboard/utils';

type LeaderboardWsLifecycle = 'connecting' | 'connected-waiting-data' | 'received' | 'error';

export default function TourLeaderboardScreen() {
  const { t } = useI18n();
  const { user } = useUser();
  const router = useRouter();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { tourId: tourParam } = useLocalSearchParams<{ tourId?: string }>();
  const [tourId, setTourId] = useState<string | null>(null);
  const [tourName, setTourName] = useState<string | null>(null);
  const [tourImageKey, setTourImageKey] = useState<string | null>(null);
  const [roundCount, setRoundCount] = useState<number | null>(null);
  const [scoreboard, setScoreboard] = useState<TourGameScore[]>([]);
  const [roundScoreboard, setRoundScoreboard] = useState<TourRoundGameScore[]>([]);
  const [fallbackTotals, setFallbackTotals] = useState<Array<{ playerId: string; totalPoints: number }>>([]);
  const [gameDefs, setGameDefs] = useState<GameDefinition[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [rounds, setRounds] = useState<RoundSummary[]>([]);
  const [totalsByHole, setTotalsByHole] = useState<TourTotalsByHoleResponse | null>(null);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [resultsTableOpen, setResultsTableOpen] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wsLifecycle, setWsLifecycle] = useState<LeaderboardWsLifecycle>('connecting');
  const [retryCount, setRetryCount] = useState(0);
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'tour-leaderboard',
      }),
    [apiBaseUrl, user?.token]
  );
  const tourImageUrl = useMemo(() => {
    if (!tourImageKey || !tourId) return null;
    if (tourImageKey.startsWith('http')) return tourImageKey;
    return `${apiBaseUrl}/api/tours/${tourId}/image`;
  }, [apiBaseUrl, tourId, tourImageKey]);

  useEffect(() => {
    const resolved = tourParam ?? getCurrentTourId();
    setTourId(resolved ?? null);
    if (resolved) {
      setCurrentTourId(resolved);
    }
  }, [tourParam]);

  useEffect(() => {
    if (!tourId || !user?.token) return;
    const socket = socketRef.current;
    const requestedTourId = String(tourId).toLowerCase();
    const startedAtMs = Date.now();
    const retryIntervalMs = 5000;
    const disconnectedErrorAfterMs = 20000;
    let snapshotReceived = false;
    let disconnectedSinceMs: number | null = null;
    let retries = 0;

    const createRequestId = () => {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }

      return `tour-lb-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
    };

    const normalizeId = (value: unknown) => String(value ?? '').trim().toLowerCase();

    socket.setAuthToken(user.token);
    socket.setSubscriptionContext({ tourId });
    setLoading(true);
    setError(null);
    setRetryCount(0);
    setWsLifecycle('connecting');

    const normalizeTour = (rawTour: Record<string, unknown>) => {
      const tourName = (rawTour.tourName as string | undefined) ?? (rawTour.name as string | undefined) ?? null;
      const imageThumbKey =
        (rawTour.imageThumbKey as string | undefined) ??
        (rawTour.ImageThumbKey as string | undefined) ??
        null;
      const imageKey = (rawTour.imageKey as string | undefined) ?? (rawTour.ImageKey as string | undefined) ?? null;
      setTourName(tourName);
      setTourImageKey(imageThumbKey ?? imageKey);
    };

    const normalizeRounds = (rawRounds: Array<Record<string, unknown>>) => {
      const normalized = rawRounds.map((round) => ({
        id: normalizeId((round.id as string | undefined) ?? (round.Id as string | undefined) ?? ''),
        courseName: (round.courseName as string | undefined) ?? (round.CourseName as string | undefined),
        name: (round.name as string | undefined) ?? (round.Name as string | undefined),
        description: (round.description as string | undefined) ?? (round.Description as string | undefined),
        status: (round.status as string | undefined) ?? (round.Status as string | undefined),
        createdAt: (round.createdAt as string | undefined) ?? (round.CreatedAt as string | undefined),
      }));
      setRounds(normalized.filter((round) => !!round.id));
      setRoundCount(normalized.length);
    };

    const normalizePlayers = (rawPlayers: Array<Record<string, unknown>>) => {
      const normalized = rawPlayers.map((player) => ({
        id: normalizeId((player.id as string | undefined) ?? (player.Id as string | undefined) ?? ''),
        name: (player.name as string | undefined) ?? (player.Name as string | undefined) ?? '',
      }));
      setPlayers(normalized.filter((player) => !!player.id));
    };

    const normalizeGames = (rawGames: Array<Record<string, unknown>>) => {
      const normalized = rawGames.map((game) => ({
        id: normalizeId((game.id as string | undefined) ?? (game.Id as string | undefined) ?? ''),
        name: (game.name as string | undefined) ?? (game.Name as string | undefined),
      }));
      setGameDefs(normalized.filter((game) => !!game.id));
    };

    const normalizeScoresByTourGame = (rows: Array<Record<string, unknown>>) => {
      const normalized = rows.map((row) => ({
        playerId: normalizeId((row.playerId as string | undefined) ?? (row.PlayerId as string | undefined) ?? ''),
        gameId: normalizeId((row.gameId as string | undefined) ?? (row.GameId as string | undefined) ?? ''),
        scorePoints: Number((row.scorePoints as number | undefined) ?? (row.ScorePoints as number | undefined) ?? 0),
      }));
      setScoreboard(normalized.filter((row) => !!row.playerId && !!row.gameId));
    };

    const normalizeScoresByRoundGame = (rows: Array<Record<string, unknown>>) => {
      const normalized = rows.map((row) => ({
        roundId: normalizeId((row.roundId as string | undefined) ?? (row.RoundId as string | undefined) ?? ''),
        courseName: (row.courseName as string | undefined) ?? (row.CourseName as string | undefined),
        playerId: normalizeId((row.playerId as string | undefined) ?? (row.PlayerId as string | undefined) ?? ''),
        gameId: normalizeId((row.gameId as string | undefined) ?? (row.GameId as string | undefined) ?? ''),
        scorePoints: Number(
          (row.scorePoints as number | undefined) ??
            (row.ScorePoints as number | undefined) ??
            (row.roundGamePoints as number | undefined) ??
            (row.RoundGamePoints as number | undefined) ??
            (row.gamesPoints as number | undefined) ??
            (row.GamesPoints as number | undefined) ??
            (row.points as number | undefined) ??
            (row.Points as number | undefined) ??
            0
        ),
      }));
      setRoundScoreboard(normalized.filter((row) => !!row.roundId && !!row.playerId));
    };

    const normalizeTotals = (rows: Array<Record<string, unknown>>) => {
      const normalized = rows
        .map((entry) => ({
          playerId: normalizeId((entry.playerId as string | undefined) ?? (entry.PlayerId as string | undefined) ?? ''),
          totalPoints: Number((entry.totalPoints as number | undefined) ?? (entry.TotalPoints as number | undefined) ?? 0),
        }))
        .filter((entry) => !!entry.playerId);
      setFallbackTotals(normalized);
    };

    const applySnapshot = (message: TourLeaderboardStateEvent) => {
      if (String(message.tourId ?? '').toLowerCase() !== requestedTourId) {
        logToBff('tourLeaderboard.ws.dropTourMismatch', { expectedTourId: tourId, messageTourId: message.tourId, type: message.type }, 'debug');
        return;
      }
      normalizeTour((message.tour ?? {}) as Record<string, unknown>);
      normalizeRounds((message.rounds ?? []) as Array<Record<string, unknown>>);
      normalizePlayers((message.players ?? []) as Array<Record<string, unknown>>);
      normalizeGames((message.games ?? []) as Array<Record<string, unknown>>);
      normalizeScoresByTourGame((message.scoresByTourGame ?? []) as Array<Record<string, unknown>>);
      normalizeScoresByRoundGame((message.scoresByRoundGame ?? []) as Array<Record<string, unknown>>);
      normalizeTotals((message.totals ?? []) as Array<Record<string, unknown>>);
      setTotalsByHole((message.totalsByHole as TourTotalsByHoleResponse | undefined) ?? null);
      if (!snapshotReceived) {
        const firstSnapshotLatencyMs = Date.now() - startedAtMs;
        logToBff('tourLeaderboard.ws.firstSnapshot', { tourId, latencyMs: firstSnapshotLatencyMs }, 'debug');
      }
      snapshotReceived = true;
      setError(null);
      setLoading(false);
      setWsLifecycle('received');
    };

    const applyUpdate = (message: TourLeaderboardUpdateEvent) => {
      if (String(message.tourId ?? '').toLowerCase() !== requestedTourId) {
        logToBff('tourLeaderboard.ws.dropTourMismatch', { expectedTourId: tourId, messageTourId: message.tourId, type: message.type }, 'debug');
        return;
      }
      normalizeScoresByTourGame((message.scoresByTourGame ?? []) as Array<Record<string, unknown>>);
      normalizeScoresByRoundGame((message.scoresByRoundGame ?? []) as Array<Record<string, unknown>>);
      normalizeTotals((message.totals ?? []) as Array<Record<string, unknown>>);
      snapshotReceived = true;
      setError(null);
      setLoading(false);
      setWsLifecycle('received');
    };

    const unsubscribeMessage = socket.onMessage((message) => {
      if (message.type === 'tour.leaderboard.state') {
        applySnapshot(message as TourLeaderboardStateEvent);
      } else if (message.type === 'tour.leaderboard.update') {
        applyUpdate(message as TourLeaderboardUpdateEvent);
      } else if (message.type === 'tour.leaderboard.error') {
        const err = message as TourLeaderboardErrorEvent;
        if (err.tourId && String(err.tourId).toLowerCase() !== requestedTourId) {
          logToBff('tourLeaderboard.ws.dropTourMismatch', { expectedTourId: tourId, messageTourId: err.tourId, type: message.type }, 'debug');
          return;
        }
        const code = String(err.code ?? '').trim();
        const isTerminal = code === 'tour.leaderboard.invalidTourId';
        if (isTerminal) {
          setError(t('tourLeaderboard.loadError'));
          setLoading(false);
          setWsLifecycle('error');
        } else {
          // Keep waiting and let interval/connection retries request a fresh snapshot.
          setError(null);
          setLoading(true);
          setWsLifecycle('connected-waiting-data');
        }
        logToBff('tourLeaderboard.ws.error', { tourId, code: err.code, message: err.message, terminal: isTerminal }, 'warn');
      }
    });

    const requestSnapshot = (reason: string) => {
      const requestId = createRequestId();
      socket.sendTourLeaderboardRequest(tourId, requestId);
      logToBff('tourLeaderboard.ws.request', { tourId, requestId, reason, apiBaseUrl, retryCount: retries }, 'debug');
      if (typeof window !== 'undefined') {
        console.info('[tour-leaderboard] ws request', { tourId, requestId, reason, apiBaseUrl, retryCount: retries });
      }
    };

    requestSnapshot('initial');
    setWsLifecycle('connected-waiting-data');

    const retryInterval = setInterval(() => {
      if (snapshotReceived) {
        return;
      }

      retries += 1;
      setRetryCount(retries);
      setWsLifecycle('connected-waiting-data');
      requestSnapshot('interval-retry');
      logToBff('tourLeaderboard.ws.retry', { tourId, retryCount: retries }, 'debug');

      if (disconnectedSinceMs && Date.now() - disconnectedSinceMs >= disconnectedErrorAfterMs) {
        setError(t('tourLeaderboard.loadError'));
        setLoading(false);
        setWsLifecycle('error');
        logToBff('tourLeaderboard.ws.prolongedDisconnect', { tourId, disconnectedMs: Date.now() - disconnectedSinceMs }, 'warn');
      }
    }, retryIntervalMs);

    const unsubscribeConnection = socket.onConnectionChange((state) => {
      logToBff('tourLeaderboard.ws.connection', { tourId, state }, 'debug');
      if (state === 'connected') {
        disconnectedSinceMs = null;
        if (!snapshotReceived) {
          setWsLifecycle('connected-waiting-data');
        }
        requestSnapshot('connected');
      } else if (!snapshotReceived) {
        if (!disconnectedSinceMs) {
          disconnectedSinceMs = Date.now();
        }
        setWsLifecycle('connecting');
      }
    });

    return () => {
      clearInterval(retryInterval);
      unsubscribeMessage();
      unsubscribeConnection();
      socket.setSubscriptionContext({ roundId: null, tourId: null });
    };
  }, [apiBaseUrl, logToBff, t, tourId, user?.token]);

  const wsStatusMessage = useMemo(() => {
    if (!loading || error) {
      return null;
    }

    if (wsLifecycle === 'connecting') {
      return t('tourLeaderboard.wsConnecting');
    }

    if (wsLifecycle === 'connected-waiting-data') {
      if (retryCount > 0) {
        return t('tourLeaderboard.wsRetrying', { count: retryCount });
      }

      return t('tourLeaderboard.wsWaitingData');
    }

    return null;
  }, [error, loading, retryCount, t, wsLifecycle]);

  const openResults = async () => {
    setResultsOpen(true);
    if (!totalsByHole) {
      setResultsError(t('tourLeaderboard.resultsError'));
      return;
    }
    setResultsLoading(false);
    setResultsError(null);
  };

  const formatRoundDate = (rawDate?: string) => {
    if (!rawDate) return null;
    const parsed = new Date(rawDate);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString();
  };

  if (!user) return <Redirect href="/login" />;

  if (!tourId) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>{t('tourLeaderboard.title')}</Text>
        <Text style={styles.meta}>{t('tourLeaderboard.missingTour')}</Text>
        <Pressable style={styles.primaryButton} onPress={() => router.push('/tournaments')}>
          <Text style={styles.primaryText}>{t('tourLeaderboard.goTours')}</Text>
        </Pressable>
      </View>
    );
  }

  const playerNameLookup = new Map(players.map((p) => [p.id, p.name]));
  const playerRows = buildPlayerRows(scoreboard, playerNameLookup, t('tourLeaderboard.unknownPlayer'));

  const orderedRounds = buildOrderedRounds(rounds);
  const roundIndexById = new Map(orderedRounds.map((round, index) => [round.id, index + 1]));
  const roundPointsByPlayerRound = new Map<string, number>();
  roundScoreboard.forEach((entry) => {
    if (!entry.playerId || !entry.roundId) {
      return;
    }
    const key = `${entry.playerId}:${entry.roundId}`;
    roundPointsByPlayerRound.set(key, (roundPointsByPlayerRound.get(key) ?? 0) + entry.scorePoints);
  });
  const fallbackRows = fallbackTotals
    .map((entry) => ({
      playerId: entry.playerId,
      name: playerNameLookup.get(entry.playerId) ?? t('tourLeaderboard.unknownPlayer'),
      roundPointsByRound: new Map(
        orderedRounds.map((round) => [
          round.id,
          roundPointsByPlayerRound.get(`${entry.playerId}:${round.id}`) ?? 0,
        ])
      ),
      total: entry.totalPoints,
    }))
    .sort((a, b) => b.total - a.total);
  const totalPerRoundRows = playerRows.map((row) => ({
    playerId: row.playerId,
    name: row.name,
    total: row.total,
    roundPointsByRound: new Map(
      orderedRounds.map((round) => [
        round.id,
        roundPointsByPlayerRound.get(`${row.playerId}:${round.id}`) ?? 0,
      ])
    ),
  }));

  const downloadCsv = async () => {
    if (!tourId || !user?.token) return;
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      setError(t('tourLeaderboard.csvUnsupported'));
      logToBff('tourLeaderboard.csv.unsupported', { tourId }, 'warn');
      return;
    }
    try {
      const response = await fetch(`${apiBaseUrl}/api/tours/${tourId}/results.csv`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!response.ok) {
        setError(t('tourLeaderboard.csvError'));
        logToBff('tourLeaderboard.csv.error', { tourId, status: response.status }, 'warn');
        return;
      }
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') ?? '';
      const fileMatch = disposition.match(/filename=\"?([^\";]+)\"?/i);
      const filename = fileMatch?.[1] ?? `tour-results.csv`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
      logToBff('tourLeaderboard.csv.downloaded', { tourId, filename });
    } catch (err) {
      setError(t('tourLeaderboard.csvError'));
      logToBff('tourLeaderboard.csv.error', { tourId, message: err instanceof Error ? err.message : String(err) }, 'warn');
    }
  };

  return (
    <ImageBackground
      source={TourLeaderboardBg}
      style={styles.background}
      imageStyle={styles.backgroundImage}
      resizeMode="cover"
    >
      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.headerCard}>
          {tourImageUrl ? (
            <Image source={{ uri: tourImageUrl }} style={styles.headerImage} resizeMode="cover" />
          ) : (
            <Image source={TrophyImage} style={styles.headerImage} resizeMode="contain" />
          )}
          <View style={styles.headerText}>
            <Text style={styles.bannerTitle}>{tourName ?? t('tourLeaderboard.title')}</Text>
            {roundCount !== null ? <Text style={styles.bannerMeta}>{t('tourLeaderboard.roundsTitle')}: {roundCount}</Text> : null}
          </View>
        </View>
        {loading ? <ActivityIndicator color="#d5e6ff" /> : null}
        {wsStatusMessage ? <Text style={styles.meta}>{wsStatusMessage}</Text> : null}
        {error ? <Text style={styles.error}>{error}</Text> : null}
      {!loading && !error && !playerRows.length && !fallbackRows.length ? <Text style={styles.meta}>{t('tourLeaderboard.empty')}</Text> : null}
      {!loading && !totalPerRoundRows.length && fallbackRows.length ? (
        <View style={styles.tableCard}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.table}>
            <View>
              <View style={styles.tableHeader}>
                <Text style={[styles.cell, styles.playerCell, styles.playerHeaderCell]}>{t('tourLeaderboard.player')}</Text>
                <Text style={[styles.cell, styles.totalCell, styles.totalCellHeader]}>{t('tourLeaderboard.total')}</Text>
                {orderedRounds.map((round, index) => (
                  <Text key={round.id} style={[styles.cell, styles.roundCell, styles.roundHeaderCell, styles.roundNumberText]}>
                    {index + 1}
                  </Text>
                ))}
              </View>
              {fallbackRows.map((row, index) => (
                <View key={row.playerId} style={[styles.tableRow, index % 2 === 0 ? styles.rowEven : styles.rowOdd]}>
                  <Text style={[styles.cell, styles.playerCell, styles.resultCell]} numberOfLines={1}>
                    {row.name}
                  </Text>
                  <Text style={[styles.cell, styles.totalCell, styles.resultCell, styles.resultText]}>{row.total}</Text>
                  {orderedRounds.map((round) => (
                    <Text
                      key={`${row.playerId}-${round.id}`}
                      style={[styles.cell, styles.roundCell, styles.resultCell, styles.resultText]}
                    >
                      {row.roundPointsByRound.get(round.id) ?? 0}
                    </Text>
                  ))}
                </View>
              ))}
            </View>
          </ScrollView>
        </View>
      ) : null}
      {totalPerRoundRows.length ? (
        <View style={styles.tableCard}>
          <View style={styles.tableHeaderRow}>
            <Text style={styles.tableHeaderTitle}>{t('tourLeaderboard.resultsHeader')}</Text>
            <View style={styles.resultsButtonRow}>
              <Pressable style={styles.resultsButton} onPress={openResults}>
                <Text style={styles.resultsButtonText}>{t('tourLeaderboard.resultsButton')}</Text>
              </Pressable>
              <Pressable style={styles.resultsButton} onPress={() => setResultsTableOpen(true)}>
                <Text style={styles.resultsButtonText}>{t('tourLeaderboard.resultsTableButton')}</Text>
              </Pressable>
              <Pressable style={styles.resultsButton} onPress={downloadCsv}>
                <Text style={styles.resultsButtonText}>{t('tourLeaderboard.downloadCsv')}</Text>
              </Pressable>
            </View>
          </View>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.table}>
            <View>
              <View style={styles.tableHeader}>
                <Text style={[styles.cell, styles.playerCell, styles.playerHeaderCell]}>{t('tourLeaderboard.player')}</Text>
                <Text style={[styles.cell, styles.totalCell, styles.totalCellHeader]}>{t('tourLeaderboard.total')}</Text>
                {orderedRounds.map((round) => (
                  <View key={`${round.id}-header`} style={[styles.gameHeaderCell, styles.roundCell]}>
                    <Text style={styles.gameHeaderText} numberOfLines={1}>
                      {roundIndexById.get(round.id) ?? '-'}
                    </Text>
                  </View>
                ))}
              </View>
              {totalPerRoundRows.map((row, index) => (
                <View key={row.playerId} style={[styles.tableRow, index % 2 === 0 ? styles.rowEven : styles.rowOdd]}>
                  <Text style={[styles.cell, styles.playerCell, styles.resultCell]} numberOfLines={1}>
                    {row.name}
                  </Text>
                  <Text style={[styles.cell, styles.totalCell, styles.resultCell, styles.resultText]}>{row.total}</Text>
                  {orderedRounds.map((round) => (
                    <Text
                      key={`${row.playerId}-${round.id}`}
                      style={[styles.cell, styles.roundCell, styles.resultCell, styles.resultText]}
                    >
                      {row.roundPointsByRound.get(round.id) ?? 0}
                    </Text>
                  ))}
                </View>
              ))}
              </View>
            </ScrollView>
          </View>
        ) : null}
      {orderedRounds.length ? (
        <View style={styles.roundsCard}>
          <Text style={styles.roundsTitle}>{t('tourLeaderboard.roundsTitle')}</Text>
          <View style={styles.roundLegendRow}>
            <Text style={styles.roundLegendLabel}>{t('tourLeaderboard.roundRowLabel')}</Text>
            <View style={styles.roundLegend}>
              {orderedRounds.map((round, index) => (
                <Text key={round.id} style={styles.roundLegendText}>
                  {index + 1}. {round.name ?? round.courseName ?? t('tourLeaderboard.roundFallback')}
                  {formatRoundDate(round.createdAt) ? ` · ${formatRoundDate(round.createdAt)}` : ''}
                </Text>
              ))}
            </View>
          </View>
        </View>
      ) : null}
      </ScrollView>
      <Modal visible={resultsOpen} transparent animationType="fade" onRequestClose={() => setResultsOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setResultsOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(event) => event.stopPropagation()}>
            <Text style={styles.modalTitle}>{t('tourLeaderboard.resultsTitle')}</Text>
            {resultsLoading ? <ActivityIndicator color="#d5e6ff" /> : null}
            {resultsError ? <Text style={styles.modalError}>{resultsError}</Text> : null}
            {totalsByHole && totalsByHole.series?.length ? (
              <ResultsGraph
                series={totalsByHole.series}
                players={players}
                holes={totalsByHole.holes ?? []}
                rounds={totalsByHole.rounds ?? []}
                lastHoleIndexByPlayer={totalsByHole.lastHoleIndexByPlayer ?? totalsByHole.LastHoleIndexByPlayer}
              />
            ) : null}
            <View style={styles.modalButtonRow}>
              <Pressable style={styles.modalSecondaryButton} onPress={() => setResultsTableOpen(true)}>
                <Text style={styles.modalSecondaryText}>{t('tourLeaderboard.resultsTableButton')}</Text>
              </Pressable>
              <Pressable style={styles.modalCloseButton} onPress={() => setResultsOpen(false)}>
                <Text style={styles.modalCloseText}>{t('tourLeaderboard.resultsClose')}</Text>
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
      <Modal visible={resultsTableOpen} transparent animationType="fade" onRequestClose={() => setResultsTableOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setResultsTableOpen(false)}>
          <Pressable style={[styles.modalCard, styles.modalTableCard]} onPress={(event) => event.stopPropagation()}>
            <Text style={styles.modalTitle}>{t('tourLeaderboard.resultsTableTitle')}</Text>
            {resultsLoading ? <ActivityIndicator color="#d5e6ff" /> : null}
            {resultsError ? <Text style={styles.modalError}>{resultsError}</Text> : null}
            {totalsByHole ? (
              <ResultsTable
                series={totalsByHole.series}
                players={players}
                holes={totalsByHole.holes}
                rounds={totalsByHole.rounds}
              />
            ) : null}
            <Pressable style={styles.modalCloseButton} onPress={() => setResultsTableOpen(false)}>
              <Text style={styles.modalCloseText}>{t('tourLeaderboard.resultsClose')}</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
    </ImageBackground>
  );
}
